import React, { useState, createContext } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import HomePage from './pages/HomePage';
import ListingDetail from './pages/ListingDetail';
import AdminDashboard from './admin/pages/AdminDashboard';
import ListingsPage from './pages/ListingsPage';
import PromoListingsPage from './pages/PromoListingsPage';
import './styles/App.css';
import BrokerDashboard from './broker/pages/BrokerDashboard';
import OrderDetail from './pages/OrderDetail';
import SetsPage from './pages/SetsPage';
import SetListingsPage from './pages/SetListingsPage';
import TermsPage from './pages/legal/TermsPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import GuaranteePage from './pages/legal/GuaranteePage';
import HomeHeader from './components/home/HomeHeader';
import TypeRail from './components/home/TypeRail';
import { AuthProvider } from './auth/AuthContext';
import AuthModalHost from './auth/AuthModalHost';
import GoogleOneTap from './auth/GoogleOneTap';
import RequireAuth from './auth/RequireAuth';

export const LoadingContext = createContext();

// The one site-wide header, rendered here for every route so it's the same
// element in the same place on every page. The homepage hides the compact
// search (it has the hero search) and the category icons (it has the side
// rail). Checkout (/listing/:certNumber) hides the category icons too.
const SiteHeader = () => {
    const { pathname } = useLocation();
    const isHome = pathname === '/';
    const isCheckout = pathname.startsWith('/listing/');
    return <HomeHeader showSearch={!isHome} showCategories={!isHome && !isCheckout} />;
};

// The floating category side rail (desktop), on every page except checkout and
// the broker and admin portals (it would sit over their tables).
const SiteRail = () => {
    const { pathname } = useLocation();
    const hidden = ['/listing/', '/brokers', '/admin'].some((p) => pathname.startsWith(p));
    return hidden ? null : <TypeRail />;
};

const AppContent = ({ setSupertype, setType, resetListings }) => {
    return (
        <div className="App">
            <Routes>
                <Route
                    path="/"
                    element={
                        <HomePage
                            setSupertype={setSupertype}
                            setType={setType}
                            resetListings={resetListings}
                        />
                    }
                />
                <Route
                    path="/listing/:certNumber"
                    element={
                        <ListingDetail
                            setSupertype={setSupertype}
                            setType={setType}
                            resetListings={resetListings}
                        />
                    }
                />
                <Route
                    path="/orders/:orderNumber"
                    element={
                        <OrderDetail
                            setSupertype={setSupertype}
                            setType={setType}
                            resetListings={resetListings}
                        />
                    }
                />
                <Route path="/pokemon" element={<ListingsPage supertype="pokemon" />} />
                <Route path="/trainer" element={<ListingsPage supertype="trainer" />} />
                <Route path="/energy" element={<ListingsPage supertype="energy" />} />
                <Route path="/promo" element={<PromoListingsPage />} />
                <Route path="/artists/:name" element={<ListingsPage />} />
                <Route path="/listings/:searchTerm" element={<ListingsPage />} />
                {/* Query-param browse (supertype/subtype/series) from the homepage TypeMenu */}
                <Route path="/listings" element={<ListingsPage />} />
                <Route path="/sets" element={<SetsPage />} />
                <Route path="/sets/:setName" element={<SetListingsPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/guarantee" element={<GuaranteePage />} />

                <Route
                    path="/brokers"
                    element={
                        <RequireAuth roles={['Broker', 'Admin']}>
                            <BrokerDashboard />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/admin"
                    element={
                        <RequireAuth roles={['Admin']}>
                            <AdminDashboard
                                setSupertype={setSupertype}
                                setType={setType}
                                resetListings={resetListings}
                            />
                        </RequireAuth>
                    }
                />
            </Routes>
        </div>
    );
};

const App = () => {
    const [supertype, setSupertype] = useState('');
    const [type, setType] = useState('');
    const [loading, setLoading] = useState(false);

    const resetListings = () => {};

    return (
        <LoadingContext.Provider value={{ loading, setLoading }}>
            <Router>
                <AuthProvider>
                    <SiteHeader />
                    <SiteRail />
                    <AppContent
                        setSupertype={setSupertype}
                        setType={setType}
                        resetListings={resetListings}
                    />
                    <AuthModalHost />
                    <GoogleOneTap />
                </AuthProvider>
            </Router>
        </LoadingContext.Provider>
    );
};

export default App;
